import { faCirclePause, faCirclePlay } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IS_SAFARI } from "app/appConstants";
import { MprcReactPlayer, MprcReactPlayerRef } from "components/MprcReactPlayer";
import { CustomVideoSlide } from "components/types/lightBox";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { OnProgressProps } from "react-player/base";
import { CardVideoSeekEvent, subscribeVideoSeekEvent, subscribeVideoTimeBackEvent, unsubscribeVideoTimeBackEvent, unubscribeVideoSeekEvent } from "views/knowledge/components/card/CardEvents";

////////////////////////////////////////////////////////////////////////////////

export type BannerControlProps = {
    offset: number;
    stopPlayback: boolean;
    videoAutoPlay?: boolean;
    onPlayback?: (playing: boolean) => void;
};

////////////////////////////////////////////////////////////////////////////////

type VideoBannerProps = CustomVideoSlide & BannerControlProps;


export const MprcVideoBanner = (props: VideoBannerProps) => {
    const {
        id, videoId, url, poster, mimetype, isAudio, isHls, offset, videoAutoPlay, tracks,
        onPlayback, stopPlayback, height, width,
        onReady, onEnded, onProgress, onSeek, onConfirmSeek, onPlay, onPause
    } = props;

    const videoRef = React.useRef<MprcReactPlayerRef>(null!);

    const [userPressPlay, setUserPressPlay] = useState<boolean>(false);
    const [ready, setReady] = useState<boolean>(false);

    const isPortrait = (height ?? 0) > (width ?? 0);

    /// ======================
    /// Eventos do Player
    /// ======================

    /// Verifica se o 'internal player' está tocando video do Youtube.
    const isYoutube = () => {
        return videoRef.current != null && videoRef.current.getInternalPlayer()?.videoTitle != null;
    };

    /// Quando video for carregado, posiciona na última posição vista.
    const handleReady = useCallback(() => {
        if (onReady) {
            var mediaPosition = onReady(id);
            if (mediaPosition) videoRef.current.seekTo(mediaPosition);
        }
        if (videoAutoPlay) setTimeout(() => setUserPressPlay(true), 300);

        setReady(true);
    }, [offset]);

    ///
    const handlePause = useCallback(() => {
        if (offset == 0 && !isYoutube()) {
            setUserPressPlay(false);
            if (onPlayback) onPlayback(false);
            if (onPause) onPause(id, videoRef.current.getCurrentTime());
        }
    }, [offset]);

    ///
    const handlePlay = useCallback(() => {
        const ct = videoRef.current.getCurrentTime();
        setUserPressPlay(true);
        onPlayback && onPlayback(true);

        if (onPlay) {
            const mediaPosition = onPlay(id, ct);
            if (mediaPosition) {
                videoRef.current.seekTo(mediaPosition);
                // Se YOUTUBE VIDEO, então deve dar um play após seek pois o player entra em modo 'pause'.
                if (isYoutube()) {
                    videoRef.current.getInternalPlayer().playVideo();
                }
            }
        }
    }, [offset]);

    /// Este evento só ocorre em vídeo MP4.
    const handleSeek = useCallback((seconds: number) => {
        if (onConfirmSeek) {
            const { playTime } = onConfirmSeek(id, seconds);
            if (playTime < seconds) videoRef.current.seekTo(playTime);
        }
        else if (onSeek) onSeek(id, seconds);
    }, [offset, videoRef]);

    ///
    const handleEnded = useCallback(() => {
        onEnded && onEnded(id, videoRef.current.getDuration());
    }, [offset]);

    /// Se Youtube, necessário checar o progresso.
    /// Este evento é disparado duranter o 'seek' do player.
    const handleProgress = useCallback((state: OnProgressProps) => {
        if (isYoutube()) {
            if (onConfirmSeek) {
                const ps = Math.floor(state.playedSeconds);
                const { playTime, threshold } = onConfirmSeek(id, ps);
                if (playTime < ps - threshold) {
                    videoRef.current.seekTo(playTime);
                }
                else if (onProgress)
                    onProgress(id, state);
            }
            else if (onProgress)
                onProgress(id, state);
        }
        else if (onProgress) {
            onProgress(id, state);
        }
    }, [offset]);

    /// Se Youtube, necessário verificar se chegou ao 'final' usando o 'seek' do player.
    const handleEnd = useCallback(() => {
        if (isYoutube()) {
            if (onConfirmSeek) {
                const duration = videoRef.current.getDuration();
                const { playTime, threshold } = onConfirmSeek(id, duration);
                if (playTime < duration - threshold) videoRef.current.seekTo(playTime);
                else handleEnded();
            }
        }
        else handleEnded();
    }, [offset]);

    /// Para Audios, manipula o click na área de Poster.
    const handleWrapClick = () => {
        if (isAudio) {
            if (userPressPlay) handlePause();
            else handlePlay();
        }
    };

    /// ============================== ///

    ///
    const sourceUrls = useMemo(() => {
        return url;
    }, [url, mimetype]);

    ///
    useEffect(() => {
        if (stopPlayback) handlePause();
    }, [stopPlayback]);

    ///
    const seekVideo = useCallback((data: CardVideoSeekEvent) => {
        if (data.detail != null && id == data.detail.videoId) {
            videoRef.current?.seekTo(data.detail.seekTime);

            var player = videoRef.current?.getInternalPlayer();
            if (player?.title) player.playVideo();
            else setUserPressPlay(true);
        }
    }, [videoRef.current, setUserPressPlay]);

    //////////////////////////////////////////////////////////////////////////////////
    /// TIME BACK CONTROL

    const videoTimeback = useCallback((data: CardVideoSeekEvent) => {
        if (id == data.detail?.videoId && data.detail?.seekTime) {
            var currentTime = videoRef.current?.getCurrentTime() ?? 0;
            var target = Math.max(currentTime - data.detail.seekTime, 0);
            if (target > 0) {
                videoRef.current?.seekTo(target);
            }
        }
    }, [videoRef.current]);

    ///
    useEffect(() => {
        subscribeVideoSeekEvent(seekVideo);
        subscribeVideoTimeBackEvent(videoTimeback);

        return () => {
            unubscribeVideoSeekEvent(seekVideo);
            unsubscribeVideoTimeBackEvent(videoTimeback);
        };
    }, []);

    //////////////////////////////////////////////////////////////////////////////////


    ///
    return (
        <div
            onClick={handleWrapClick}
            style={{ width: "inherit", height: "inherit", cursor: "pointer" }}
        >

            {ready && isAudio &&
                <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }} >
                    {userPressPlay
                        ? <FontAwesomeIcon icon={faCirclePause} size="5x" color="white" />
                        : <FontAwesomeIcon icon={faCirclePlay} size="5x" color="white" />
                    }
                </div>
            }

            <MprcReactPlayer
                id={id}
                videoId={videoId}
                playing={(offset == 0 && userPressPlay)}
                url={sourceUrls}
                width={"100%"}
                height={"100%"}
                ref={videoRef}
                onReady={handleReady}
                onProgress={handleProgress}
                onEnded={handleEnd}
                onSeek={handleSeek}
                onPlay={handlePlay}
                onPause={handlePause}
                config={{
                    file: {
                        attributes: {
                            poster: poster,
                            width: !isPortrait ? "100%" : width ?? "100%",
                            height: !isPortrait ? "100%" : height ?? "100%",
                            disablePictureInPicture: true,
                            controlsList: "nodownload",
                            paudioonly: isAudio ? "true" : "false",
                            crossOrigin: "anonymous"
                        },
                        forceAudio: isAudio,
                        forceHLS: isHls && !IS_SAFARI,
                        tracks: tracks ?? []
                    }
                }}
            />
        </div >
    );
};
